import { useState } from 'react';
import ExcelJS from 'exceljs';
import { Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import * as FileSaver from 'file-saver';
import useCommonStore from 'stores/useCommon';
import { formatCurrency, toLocalTime } from 'utils';
import { BranchesType } from 'services/api/type/branch.type';

interface Props {
  fileName: string;
  dataToExport: any[];
  t: (key: string) => string;
  fromDate: string;
  toDate?: string;
  selectedBranchesData?: BranchesType[];
}

const ExportServicesReport = ({
  fileName,
  dataToExport,
  t,
  fromDate,
  toDate,
  selectedBranchesData = []
}: Props) => {
  console.log('selectedBranchesData', selectedBranchesData);
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const { currentBranch } = useCommonStore();
  const currencyLabel = currentBranch?.currency_name ?? 'VND';

  const fileType =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const fileExtension = '.xlsx';

  const exportToCSV = async () => {
    try {
      setIsLoadingExport(true);

      const workbook = new ExcelJS.Workbook();
      const sheetName = 'Services Report';
      const sheet = workbook.addWorksheet(sheetName, {
        views: [
          {
            showGridLines: false
          }
        ]
      });

      sheet.mergeCells('A1:L1');
      sheet.getCell('A1').value = 'BÁO CÁO DỊCH VỤ';
      sheet.getCell('A1').font = { size: 11, bold: true };
      sheet.getCell('A1').alignment = { horizontal: 'center' };

      // Date range
      sheet.getCell('A3').value = `Ngày`;
      sheet.getCell('A3').font = { bold: true };
      sheet.getCell('B3').value = fromDate;
      sheet.getCell('C3').value = `Đến`;
      sheet.getCell('C3').font = { bold: true };
      sheet.getCell('D3').value = toDate;

      // Selected branches list
      let lastBranchRow = 3; // Default to row 3 if no branches
      if (selectedBranchesData && selectedBranchesData.length > 0) {
        sheet.getCell('E3').value = 'Chi nhánh đã chọn';
        sheet.getCell('E3').font = { bold: true };

        selectedBranchesData.forEach((branch, index) => {
          const row = 4 + index;
          sheet.getCell(`E${row}`).value = `${index + 1}. ${branch.name}`;
          lastBranchRow = row; // Track the last row with branch data
        });
      }

      // Calculate header row position: start after the last branch row + 1 row spacing
      const headerRowPosition = lastBranchRow + 2;

      // Header row
      sheet.getRow(headerRowPosition).values = [
        'ID',
        'Chi nhánh',
        'Đơn hàng',
        'Mã đặt phòng',
        'Mã chi tiết đặt phòng',
        'Tên sản phẩm/Dịch vụ',
        'Mã sản phẩm',
        `Tổng giá (${currencyLabel})`,
        `Chiết khấu (${currencyLabel})`,
        'Số lượng',
        'Ngày giao dịch'
      ];

      sheet.getRow(headerRowPosition).eachCell(cell => {
        cell.border = {
          top: { style: 'medium', color: { argb: 'FF000000' } },
          bottom: { style: 'medium', color: { argb: 'FF000000' } },
          left: { style: 'medium', color: { argb: 'FF000000' } },
          right: { style: 'medium', color: { argb: 'FF000000' } }
        };
        cell.font = { bold: true };
        cell.alignment = { horizontal: 'center' };
      });

      // Set column widths
      sheet.columns = [
        { key: 'id', width: 10 },
        { key: 'branch_name', width: 20 },
        { key: 'sale_order_name', width: 20 },
        { key: 'booking_id', width: 15 },
        { key: 'booking_line_id', width: 18 },
        { key: 'product_name', width: 30 },
        { key: 'product_id', width: 12 },
        { key: 'price', width: 18 },
        { key: 'discount', width: 18 },
        { key: 'quantity', width: 12 },
        { key: 'transaction_date', width: 18 }
      ];

      // Calculate totals
      const totals = dataToExport.reduce(
        (acc, item) => ({
          totalPrice: acc.totalPrice + (item.price * item.quantity || 0),
          totalDiscount: acc.totalDiscount + (item.discount || 0),
          totalQuantity: acc.totalQuantity + (item.quantity || 0)
        }),
        { totalPrice: 0, totalDiscount: 0, totalQuantity: 0 }
      );

      // Add data rows
      dataToExport.forEach(item => {
        const row = sheet.addRow({
          id: item.id,
          branch_name: item.branch_name || '',
          sale_order_name: item.sale_order_name || '',
          booking_id: item.booking_id,
          booking_line_id: item.booking_line_id,
          product_name: item.product_name,
          product_id: item.product_id,
          price: formatCurrency(item.price * item.quantity || 0),
          discount: formatCurrency(item.discount),
          quantity: item.quantity,
          transaction_date: item.transaction_date
            ? toLocalTime(item.transaction_date, 'DD/MM/YYYY')
            : ''
        });

        row.eachCell({ includeEmpty: true }, (cell: any) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          // Align product_name to left, others to center
          if (cell.col === 6) {
            cell.alignment = { horizontal: 'left' };
          } else {
            cell.alignment = { horizontal: 'center' };
          }
        });
      });

      // Add total row
      const totalRow = sheet.addRow({
        id: '',
        branch_name: '',
        sale_order_name: '',
        booking_id: '',
        booking_line_id: '',
        product_name: '',
        product_id: 'TỔNG CỘNG',
        price: formatCurrency(totals.totalPrice),
        discount: formatCurrency(totals.totalDiscount),
        quantity: totals.totalQuantity,
        transaction_date: ''
      });

      totalRow.eachCell({ includeEmpty: true }, (cell: any) => {
        cell.border = {
          top: { style: 'medium' },
          left: { style: 'thin' },
          bottom: { style: 'medium' },
          right: { style: 'thin' }
        };
        cell.font = { bold: true };
        cell.alignment = { horizontal: 'center' };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const data = new Blob([buffer], { type: fileType });
      FileSaver.saveAs(data, fileName + fileExtension);
    } catch (error) {
      console.error('Export Error:', error);
    } finally {
      setIsLoadingExport(false);
    }
  };

  return (
    <Button icon={<DownloadOutlined />} onClick={exportToCSV} loading={isLoadingExport}>
      {t('common.actions.exportData')}
    </Button>
  );
};

export default ExportServicesReport;
